import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Refrigerator, WashingMachine, Tv, Microwave, Ticket, CalendarDays, QrCode, Store, ShieldCheck, Gift, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const APPLIANCES = [
  { Icon: Refrigerator, label: "Refrigerador", from: "#0055FF", to: "#4d8bff", rot: -8, x: 0, y: 0, delay: 0 },
  { Icon: WashingMachine, label: "Lavadora", from: "#FF007A", to: "#ff6ab3", rot: 6, x: 74, y: 34, delay: 0.15 },
  { Icon: Tv, label: 'Pantalla 50"', from: "#10b981", to: "#6ee7b7", rot: -4, x: 14, y: 96, delay: 0.3 },
  { Icon: Microwave, label: "Microondas", from: "#f59e0b", to: "#fcd34d", rot: 7, x: 92, y: 118, delay: 0.45 },
];

function FloatingAppliances() {
  return (
    <div className="relative w-[200px] h-[200px] shrink-0 mx-auto sm:mx-0" aria-hidden>
      {APPLIANCES.map((a, i) => (
        <motion.div key={a.label}
          initial={{ opacity: 0, y: 24, scale: 0.7, rotate: a.rot - 12 }}
          animate={{ opacity: 1, y: [0, -7, 0], scale: 1, rotate: a.rot }}
          transition={{ opacity: { delay: a.delay, duration: 0.45 }, scale: { delay: a.delay, duration: 0.45 }, rotate: { delay: a.delay, duration: 0.45 }, y: { delay: a.delay + 0.6, duration: 3 + i * 0.35, repeat: Infinity, ease: "easeInOut" } }}
          className="absolute w-[96px] rounded-2xl p-2.5 text-white shadow-2xl"
          style={{ left: a.x, top: a.y, background: `linear-gradient(135deg, ${a.from}, ${a.to})`, zIndex: 10 - i }}>
          <a.Icon className="w-7 h-7 drop-shadow" />
          <div className="text-[10px] font-extrabold mt-1 leading-tight">{a.label}</div>
        </motion.div>
      ))}
      <motion.div initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: -12 }} transition={{ delay: 0.8, type: "spring", stiffness: 180 }}
        className="absolute -left-4 -top-4 rounded-xl px-2.5 py-1.5 text-[#2b1a00] shadow-xl flex items-center gap-1" style={{ background: "linear-gradient(135deg,#fff3b0,#f7c948 50%,#d69e2e)", zIndex: 20 }}>
        <Ticket className="w-4 h-4" /><span className="font-mono font-black text-[10px] tracking-widest">BOLETO 202610-00421</span>
      </motion.div>
    </div>
  );
}

/** Callout del SORTEO MENSUAL de electrodomésticos para el hero. variant: "b2c" | "b2b" */
export default function RaffleHeroCallout({ variant = "b2c" }) {
  const b2c = variant === "b2c";
  const accent = b2c ? "#FF007A" : "#0055FF";
  const [raffle, setRaffle] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/raffle`).then((r) => r.json()).then((d) => setRaffle(d?.current || null)).catch(() => {});
  }, []);

  const prize = raffle?.title || "Electrodoméstico sorpresa";
  const drawDay = raffle?.draw_date ? new Date(raffle.draw_date + "T12:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : "10 de noviembre de 2026";
  const isFirst = raffle ? !!raffle.is_first_draw : true;

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
      className="mt-4 relative rounded-3xl p-[2px] max-w-xl overflow-hidden"
      style={{ background: "linear-gradient(120deg,#10b981,#f7c948,#0055FF,#10b981)", backgroundSize: "300% 300%", animation: "hr-border-flow 7s ease infinite" }}
      data-testid={`hero-raffle-${variant}-callout`}>
      <style>{`@keyframes hr-border-flow{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}`}</style>
      <div className="relative rounded-[22px] bg-[#07111f] text-white p-5 sm:p-6 overflow-hidden">
        <div className="absolute -top-24 -right-12 w-64 h-64 rounded-full bg-emerald-400/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-10 w-64 h-64 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: accent }} />
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

        <div className="relative flex flex-wrap items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-[#07111f] px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]"><Gift className="w-3 h-3" /> Sorteo mensual</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]"><CalendarDays className="w-3 h-3 text-[#f7c948]" /> {isFirst ? "Primer sorteo" : "Próximo sorteo"}: {drawDay}</span>
        </div>

        <div className="relative grid sm:grid-cols-[1fr_auto] gap-5 items-center">
          <div>
            {b2c ? (
              <>
                <h3 className="font-heading font-black text-2xl sm:text-3xl leading-[1.05] tracking-tight">
                  Cada compra te da un <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#6ee7b7,#f7c948)" }}>boleto para ganar electrodomésticos.</span>
                </h3>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">
                  Compras en un negocio participante, <strong className="text-white">escaneas su QR de boletos</strong> y al instante cae un boleto digital en tu billetera. <strong className="text-white">Un boleto por escaneo</strong> — y puedes repetir en el mismo negocio en cada compra. Acumula boletos de todos los comercios que visites: entre más compres, más chances. Sorteamos el <strong className="text-[#f7c948]">día 10 de cada mes</strong> — {isFirst ? <>el <strong className="text-white">primer sorteo es el {drawDay}</strong> y todos los boletos que juntes desde hoy participan</> : <>próximo sorteo: <strong className="text-white">{drawDay}</strong></>}. Premio: <strong className="text-white">{prize}</strong>.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-heading font-black text-2xl sm:text-3xl leading-[1.05] tracking-tight">
                  Regala un boleto en cada compra. <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg,#4d8bff,#6ee7b7)" }}>El premio lo pagamos nosotros.</span>
                </h3>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">
                  Con tu <strong className="text-white">QR de boletos</strong> premias a cada cliente que te compra con un boleto para el <strong className="text-white">Sorteo Mensual de electrodomésticos</strong> (refrigeradores, lavadoras, pantallas…). Tus clientes regresan para juntar más boletos. El sorteo lo organiza <strong className="text-[#f7c948]">MARKETING DE INTELIGENCIA ARTIFICIAL</strong> y <strong className="text-white">los premios los paga nuestra empresa, no el comercio</strong>: para ti cuesta cero. {isFirst ? <>El <strong className="text-white">primer sorteo será el {drawDay}</strong>; todos los boletos que entregues desde hoy participan.</> : <>Próximo sorteo: <strong className="text-white">{drawDay}</strong>.</>}
                </p>
              </>
            )}

            <div className="mt-4 grid grid-cols-3 gap-2">
              {(b2c
                ? [[QrCode, "1 escaneo = 1 boleto", "repite en cada compra"], [Store, "Todos los comercios", "acumula de varios"], [Gift, isFirst ? "1er sorteo 10 nov 2026" : "Sorteo el día 10", "premio garantizado"]]
                : [[QrCode, "Tu QR de boletos", "imprímelo en caja"], [ShieldCheck, "Costo cero", "premios pagados por nosotros"], [Sparkles, "Clientes que vuelven", "por más boletos"]]
              ).map(([Icon, t, d]) => (
                <div key={t} className="rounded-xl bg-white/[0.06] border border-white/10 p-2.5">
                  <Icon className="w-4 h-4 text-emerald-300" />
                  <div className="text-xs font-extrabold mt-1 leading-tight">{t}</div>
                  <div className="text-[10px] text-white/60 leading-tight">{d}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="rounded-2xl px-4 py-2 bg-white/10 border border-white/15">
                <div className="text-[9px] uppercase tracking-widest font-black text-white/60">{isFirst ? "Premio del primer sorteo" : "Premio de este mes"}</div>
                <div className="font-heading font-black text-lg leading-none text-[#f7c948]" data-testid={`hero-raffle-${variant}-prize`}>{prize}</div>
              </div>
              {b2c ? (
                <Link to="/mi/boletos" data-testid="hero-raffle-b2c-cta">
                  <Button className="h-11 rounded-full font-bold text-[#07111f] bg-emerald-400 hover:bg-emerald-300 shadow-lg hover:scale-[1.03] transition-transform"><Ticket className="w-4 h-4" /> Ver mis boletos <ArrowRight className="w-4 h-4" /></Button>
                </Link>
              ) : (
                <Link to="/register" data-testid="hero-raffle-b2b-cta">
                  <Button className="h-11 rounded-full font-bold text-[#07111f] bg-emerald-400 hover:bg-emerald-300 shadow-lg hover:scale-[1.03] transition-transform"><Gift className="w-4 h-4" /> Quiero regalar boletos <ArrowRight className="w-4 h-4" /></Button>
                </Link>
              )}
            </div>
            {!b2c && <p className="mt-3 text-[10px] text-white/50 leading-snug">Sorteo organizado y financiado por MARKETING DE INTELIGENCIA ARTIFICIAL. El comercio únicamente entrega boletos digitales; no paga ni entrega premios. Incluido en ambos paquetes.</p>}
          </div>

          <FloatingAppliances />
        </div>
      </div>
    </motion.div>
  );
}
